// Kitchen flow lane D — pure purchasing rules for the frontend. The server is
// the authority; these mirror it so screens can show the same numbers before
// a round-trip (and so the rules are pinned by vitest).

import type { FromSuggestionsResult } from '@/types/kitchenFlowPurchasing'

const EPS = 1e-9

function roundQty(q: number): number {
  return Math.round(q * 1000) / 1000
}

/** Mirror of backend parSuggestedOrder (stock_purchasing_kitchen_flow.go).
 *  Position = on hand at store locations + still on order. Order when the
 *  position has fallen to the minimum; top up to the maximum. `null` means no
 *  par is set and the old reorder-level rule applies instead. */
export function parSuggestedOrder(
  minQty: number,
  maxQty: number | null,
  storeOnHand: number,
  onOrder: number,
): { needs: boolean; qty: number } | null {
  if (maxQty == null) return null
  const qty = Math.max(0, maxQty - storeOnHand - onOrder)
  const position = storeOnHand + onOrder
  return { needs: position <= minQty + EPS && qty > EPS, qty: roundQty(qty) }
}

/** Mirror of backend suggestedReorderQty (stock.go): reach the larger of 2×
 *  the reorder level or 14 days of use, net of on hand and on order. */
export function suggestedReorderQty(onHand: number, reorderLevel: number, avgDailyUsage: number, onOrder: number): number {
  const target = Math.max(reorderLevel * 2, Math.ceil(avgDailyUsage * 14))
  return Math.max(0, target - onHand - onOrder)
}

/** Validation for the par editor: both blank clears; otherwise non-negative
 *  numbers with most ≥ least. */
export function parInputError(minText: string, maxText: string): 'negative' | 'max_below_min' | 'not_a_number' | null {
  const blank = (s: string) => s.trim() === ''
  if (blank(minText) && blank(maxText)) return null
  const min = blank(minText) ? 0 : Number(minText)
  const max = blank(maxText) ? null : Number(maxText)
  if (!Number.isFinite(min) || (max != null && !Number.isFinite(max))) return 'not_a_number'
  if (min < 0 || (max != null && max < 0)) return 'negative'
  if (max != null && max < min) return 'max_below_min'
  return null
}

/** Mirror of backend parsePOApprovers + poApproverAllowed: may this role
 *  approve a purchase order? Admin always; anyone else only with the kitchen
 *  flow on and named in app_settings.po_approvers (default ["admin"]). */
export function canApprovePO(role: string | null | undefined, approversSetting: unknown, kitchenFlow: boolean): boolean {
  if (role === 'admin') return true
  if (!kitchenFlow || !role) return false
  return parsePOApprovers(approversSetting).includes(role)
}

export function parsePOApprovers(setting: unknown): string[] {
  if (!Array.isArray(setting)) return ['admin']
  const allowed = new Set(['admin', 'inventory_manager'])
  const out = Array.from(new Set(setting.filter((r): r is string => typeof r === 'string' && allowed.has(r.trim())).map((r) => r.trim())))
  return out.length ? out : ['admin']
}

/** Percentage change prev → cur, rounded to 2 dp; null without a prev price. */
export function priceChangePct(prev: number, cur: number): number | null {
  if (!(prev > 0)) return null
  return Math.round(((cur - prev) / prev) * 10000) / 100
}

/** SVG polyline points for a sparkline of prices given oldest → newest. */
export function sparklinePoints(values: number[], width: number, height: number, pad = 2): string {
  if (values.length === 0) return ''
  if (values.length === 1) {
    const y = height / 2
    return `${pad},${y} ${width - pad},${y}`
  }
  const lo = Math.min(...values)
  const hi = Math.max(...values)
  const span = hi - lo || 1
  const step = (width - pad * 2) / (values.length - 1)
  return values
    .map((v, i) => {
      const x = pad + i * step
      const y = hi === lo ? height / 2 : pad + (1 - (v - lo) / span) * (height - pad * 2)
      return `${Math.round(x * 10) / 10},${Math.round(y * 10) / 10}`
    })
    .join(' ')
}

/** Receiving stepper: move a received quantity by delta, never below 0 and
 *  never above what is still to come. */
export function stepReceivedQty(current: number, delta: number, open: number): number {
  const next = Math.round((current + delta) * 1000) / 1000
  return Math.min(Math.max(next, 0), Math.round(open * 1000) / 1000)
}

/** How big a +/− step should be: 1 for count-like units and big quantities,
 *  a tenth for small weights/volumes. */
export function stepSizeFor(unit: string, open: number): number {
  const u = unit.toLowerCase()
  if (['kg', 'l', 'ltr', 'litre', 'liter'].includes(u) && open < 10) return 0.5
  return 1
}

/** "Expected 10 bags" when the item has a purchase unit (lane A columns, if
 *  present on the line), else "Expected 10 kg". factor = base units per pack. */
export function expectedLabel(open: number, unit: string, packLabel?: string | null, packFactor?: number | null): string {
  if (packLabel && packFactor && packFactor > 0) {
    const packs = open / packFactor
    if (Math.abs(packs - Math.round(packs)) < 1e-6) {
      return `${Math.round(packs)} × ${packLabel} (${fmt(open)} ${unit})`
    }
  }
  return `${fmt(open)} ${unit}`
}

function fmt(n: number): string {
  return String(Math.round(n * 1000) / 1000)
}

export interface SuggestionPick {
  stock_item_id: string
  qty: number
  supplier_id: string | null
}

/** Group picked suggestion lines by vendor for the confirm screen — the same
 *  grouping the server applies (items without a vendor listed separately). */
export function groupPicksByVendor(picks: SuggestionPick[]): { byVendor: Map<string, SuggestionPick[]>; unassigned: SuggestionPick[] } {
  const byVendor = new Map<string, SuggestionPick[]>()
  const unassigned: SuggestionPick[] = []
  for (const p of picks) {
    if (!(p.qty > 0)) continue
    if (!p.supplier_id) {
      unassigned.push(p)
      continue
    }
    const list = byVendor.get(p.supplier_id) ?? []
    list.push(p)
    byVendor.set(p.supplier_id, list)
  }
  return { byVendor, unassigned }
}

/** What POST /store/purchase-orders/from-suggestions answered. A replayed
 *  idempotency key comes back as `{ idempotent_replay: true }` with no
 *  purchase_orders — the orders were made by the first tap — so callers must
 *  treat it as done and refetch, never read `purchase_orders` off it. */
export function fromSuggestionsOutcome(
  data: Partial<FromSuggestionsResult> | null | undefined,
): { kind: 'replay' } | { kind: 'made'; result: FromSuggestionsResult } {
  if (!data || data.idempotent_replay || !Array.isArray(data.purchase_orders)) return { kind: 'replay' }
  return { kind: 'made', result: { purchase_orders: data.purchase_orders, unassigned: data.unassigned ?? [] } }
}

/** One idempotency key per (requisition, shortfall content): the same
 *  shortfall tapped twice — a double tap, a retry after a lost answer, a
 *  remount — reuses the key, so the server makes the orders once. A changed
 *  shortfall (after buying, or new stock) gets a new key. */
export function shortfallSignature(
  requisitionId: string,
  lines: { stock_item_id: string; qty: number; supplier_id?: string }[],
): string {
  const parts = lines
    .map((l) => `${l.stock_item_id}:${roundQty(l.qty)}:${l.supplier_id ?? ''}`)
    .sort()
  return `${requisitionId}|${parts.join(',')}`
}

export function keyForSignature(store: Map<string, string>, signature: string, mint: () => string): string {
  let k = store.get(signature)
  if (!k) {
    k = mint()
    store.set(signature, k)
  }
  return k
}
